import {
  FleetDataModel,
  HealthDirection,
  MissionCodeType,
  MissionDateFilter,
} from 'models/enums/fleet-management';
import { HEALTH_STATUS } from './FleetWidget';
import { FleetDataFilter } from './FleetDataFilter';

interface FleetDataSet {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  model: FleetDataModel;
  fields: string[] | null;
  missionDateFilter: MissionDateFilter;
  start: string | null;
  end: string | null;
  filters: FleetDataFilter[];
}

export const emptyDataset: FleetDataSet = {
  id: '',
  name: '',
  description: null,
  isDefault: false,
  model: FleetDataModel.MISSION,
  fields: [],
  missionDateFilter: MissionDateFilter.MONTH,
  start: null,
  end: null,
  filters: [],
};

export default FleetDataSet;

export interface FleetDataRobots {
  id: string;
  name: string;
  serial_number: string;
  customer: string | null;
  dealer: string;
  dealer_id: string;
  deployment_start: string;
  software_version__rsw_software_version: string | null;
  hardware_version__robot_generation: string | null;
  total_duration: number;
  last_successful_mission_date: string | null;
  mission_count: number;
  total_area: number;
  customer_journey_status: string; // robot health field
}

/**
 * The robot fields the header and the built-in pages read. Asking for them by name keeps the
 * answer to a few hundred bytes a robot instead of every hardware and software column the
 * default robot dataset carries, so it stays within the cache limit on a large fleet.
 */
export const HEADER_ROBOT_FIELDS = [
  'id',
  'name',
  'serial_number',
  'customer',
  'dealer',
  'customer_journey_status',
  'mission_count',
  'total_area',
  'total_duration',
  'last_successful_mission_date',
  'software_version__rsw_software_version',
  'hardware_version__robot_generation',
];

export interface FleetDataMissions {
  week_start: string;
  total_area: number;
  /** Hours the week's missions ran. */
  total_duration: number | null;
}

export interface FleetDataHealth {
  week_start: string;
  active_robot_count: number;
  green_count: number;
  green_percentage: number;
  status_counts: {
    GREEN: number;
    ORANGE: number;
    RED: number;
    GREY: number;
  };
  /** How the week's robots moved since the week before it; the five add up to the active robots. */
  improved_count: number;
  deteriorated_count: number;
  went_dark_count: number;
  unchanged_count: number;
  joined_count: number;
}

type RobotHealthStatus = Exclude<HEALTH_STATUS, HEALTH_STATUS.ALL>;

/*
 * The answers of the health and performance dashboards' own endpoints (GET /fleet/insights/...).
 */

/** Identity and organization labels, read from the robot's open deployment. */
export interface FleetDataRobotLabels {
  id: string;
  name: string;
  serial_number: string;
  customer: string | null;
  dealer: string | null;
  model: string | null;
  software_version__rsw_software_version: string | null;
  hardware_version__robot_generation: string | null;
}

/** One robot's colour at the window start against the window end. */
export interface FleetDataHealthTransition extends FleetDataRobotLabels {
  /** Null when the organization took the robot on inside the window. */
  previous_status: RobotHealthStatus | null;
  new_status: RobotHealthStatus;
  direction: HealthDirection;
  changed_at: string | null;
  last_successful_mission_date: string | null;
  days_since_success: number | null;
  green_limit_days: number;
  /** Codes on the last mission that ended by the window end. */
  end_error_codes: string[];
  end_maintenance_codes: string[];
  /** Every code reported inside the window. */
  window_error_codes: string[];
  window_maintenance_codes: string[];
}

/** A code the fleet reported, ranked by the robots that reported it. */
export interface FleetDataMissionCode {
  code_type: MissionCodeType;
  code: string;
  robot_count: number;
  /** Distinct missions that reported the code. */
  occurrences: number;
  end_occurrences: number;
  run_occurrences: number;
}

/** GET fleet/insights/health-changes/ */
export interface HealthChangesAnswer {
  transitions: FleetDataHealthTransition[];
  codes: FleetDataMissionCode[];
}

/** GET fleet/insights/performance-changes/: last complete week's area against a four-week baseline. */
export interface FleetDataPerformanceChange extends FleetDataRobotLabels {
  week_start: string;
  last_week_area: number;
  baseline_weekly_area: number | null;
  ratio: number | null;
  has_baseline: boolean;
  operating_hours: number;
  mission_count: number;
  /** The robot's last successful mission by the window end. */
  last_successful_mission_date: string | null;
  customer_journey_status: RobotHealthStatus;
}

/** GET fleet/insights/reliability/: the fleet's missions in one Mon-Sun week. */
export interface FleetDataReliabilityWeek {
  week_start: string;
  mission_count: number;
  successful_count: number;
  /** Hours. */
  total_duration: number;
  failures_count: number;
}

/** One robot's output in one Mon-Sun week. */
export interface FleetDataRobotWeek {
  week_start: string;
  cleaned_area: number;
  duration_hours: number;
  cleaning_performance: number | null;
  mission_count: number;
  rsw_versions: string[];
}

/** How many of one robot's missions reported a code in one week. */
export interface FleetDataRobotWeekCode {
  code_type: MissionCodeType;
  code: string;
  week_start: string;
  occurrences: number;
}

/** GET fleet/insights/<robot id>/robot-performance/ */
export interface RobotPerformanceAnswer {
  weeks: FleetDataRobotWeek[];
  codes: FleetDataRobotWeekCode[];
}

/** Every row shape POST /fleet/datasets/data/ answers with, one per dataset model. */
export type FleetDataRows = FleetDataRobots[] | FleetDataMissions[] | FleetDataHealth[];
